import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import AnimatedLine from '../components/AnimatedLine';

const Methodology = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl sm:text-6xl md:text-7xl font-bold mb-8"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            We Don't Guess. We Test.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl sm:text-2xl text-white/70 mb-12"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We are not artists. We are attention engineers. Our process is built on data, not feelings.
          </motion.p>
        </div>
      </section>

      <AnimatedLine />

      {/* The Process */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors"
            >
              <div className="text-6xl font-bold text-purple-400 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                01
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                The Signal Extraction
              </h3>
              <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                We don't need your time; we need your brain. We use 60-minute "Extraction Protocols" to download your expertise.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors"
            >
              <div className="text-6xl font-bold text-purple-400 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                02
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                The AI Arbitrage
              </h3>
              <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                We utilize a proprietary stack of AI tools to slice, transcript, and repackage your IP 10x faster than traditional agencies.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors"
            >
              <div className="text-6xl font-bold text-purple-400 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                03
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                The Retention Science
              </h3>
              <p className="text-white/70 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                Every frame is analyzed. We track drop-off points, hook efficacy, and algorithmic triggers. If it doesn't hold attention, it dies in the lab.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Retention Curve Visualization */}
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold mb-12 text-center"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Anatomy of a High-Retention Video
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-lg p-8 bg-white/5"
          >
            {/* Simulated Retention Curve */}
            <div className="relative h-64 mb-8">
              <svg viewBox="0 0 800 200" className="w-full h-full">
                {/* Grid lines */}
                <line x1="0" y1="50" x2="800" y2="50" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                <line x1="0" y1="100" x2="800" y2="100" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                <line x1="0" y1="150" x2="800" y2="150" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                
                {/* Retention curve */}
                <path
                  d="M 0 20 L 100 25 L 200 30 L 300 40 L 400 50 L 500 70 L 600 90 L 700 120 L 800 160"
                  fill="none"
                  stroke="#8429C3"
                  strokeWidth="3"
                />
                
                {/* Annotations */}
                <circle cx="100" cy="25" r="5" fill="#8429C3" />
                <text x="100" y="10" fill="white" fontSize="12" textAnchor="middle" style={{ fontFamily: 'Inter, sans-serif' }}>Hook</text>
                
                <circle cx="400" cy="50" r="5" fill="#8429C3" />
                <text x="400" y="35" fill="white" fontSize="12" textAnchor="middle" style={{ fontFamily: 'Inter, sans-serif' }}>Payoff</text>
                
                <circle cx="700" cy="120" r="5" fill="#8429C3" />
                <text x="700" y="105" fill="white" fontSize="12" textAnchor="middle" style={{ fontFamily: 'Inter, sans-serif' }}>CTA</text>
              </svg>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <div className="font-semibold text-white mb-2">0-3 Seconds: The Hook</div>
                <div className="text-white/70">Pattern interrupt. Visual contrast. Immediate value proposition.</div>
              </div>
              <div>
                <div className="font-semibold text-white mb-2">Middle: The Payoff</div>
                <div className="text-white/70">Deliver on the promise. Maintain pacing. Strategic loops.</div>
              </div>
              <div>
                <div className="font-semibold text-white mb-2">End: The CTA</div>
                <div className="text-white/70">Clear next step. Algorithmic signal. Retention anchor.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Data-Driven Approach */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold mb-8"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Every Decision is Auditable
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-white/70 mb-12"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We don't rely on "creative intuition." Every edit, every thumbnail, every hook is tested against retention data. If it doesn't perform, we kill it and iterate.
          </motion.p>
          
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            href="#contact"
            className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Start The Protocol Sprint
          </motion.a>
        </div>
      </section>

      <AnimatedLine />

      {/* Footer */}
      <footer className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2024 AttentionLabs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Methodology;
